import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const Contact = () => {
  const location = useLocation();
  const [activeForm, setActiveForm] = useState('general');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    if (type) setActiveForm(type);
  }, [location]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    // Netlify forms logic is handled via the 'netlify' attribute
  };

  const forms = {
    general: {
      title: "Strategic Consulting Enquiry",
      desc: "For Senior Leaders seeking operational excellence.",
      id: "consulting-form"
    },
    training: {
      title: "Training Programme Application",
      desc: "Mental Health & Complex Needs Excellence.",
      id: "training-form"
    },
    mentoring: {
      title: "Leadership Mentoring Application",
      desc: "10-week intensive for CEOs & Managers.",
      id: "mentoring-form"
    }
  };

  return (
    <div className="pt-20 pb-32 bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif font-bold text-navy-900 mb-4">Work With Us</h1>
          <p className="text-slate-600">Choose the service you're interested in to begin your application.</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-12 justify-center">
          {Object.entries(forms).map(([key, form]) => (
            <button
              key={key}
              onClick={() => { setActiveForm(key); setSubmitted(false); }}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                activeForm === key 
                ? 'bg-navy-900 text-gold-500 shadow-lg' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {form.title.split(' ')[0]} {form.title.split(' ')[1]}
            </button>
          ))}
        </div>

        {submitted ? (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white p-12 rounded-2xl shadow-xl text-center border border-green-100"
          >
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon name="Check" className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-serif font-bold text-navy-900 mb-4">Application Received</h2>
            <p className="text-slate-600 max-w-sm mx-auto">
              Thank you. Your application for {forms[activeForm].title} has been received. Yeukai will be in touch within 2 business days.
            </p>
            <button 
              onClick={() => setSubmitted(false)}
              className="mt-8 text-navy-900 font-bold hover:underline"
            >
              Submit another enquiry
            </button>
          </motion.div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200">
            <div className="bg-navy-900 p-8 text-white">
              <h2 className="text-2xl font-serif font-bold text-gold-500 mb-2">{forms[activeForm].title}</h2>
              <p className="text-slate-300">{forms[activeForm].desc}</p>
            </div>
            
            <form 
              name={forms[activeForm].id}
              method="POST" 
              data-netlify="true"
              onSubmit={handleSubmit}
              className="p-8 space-y-6"
            >
              <input type="hidden" name="form-name" value={forms[activeForm].id} />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-900">Full Name *</label>
                  <input type="text" name="name" required className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-900">Job Title *</label>
                  <input type="text" name="job_title" required className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-900">Organisation Name *</label>
                  <input type="text" name="organisation" required className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-900">Email Address *</label>
                  <input type="email" name="email" required className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                </div>
              </div>

              {activeForm === 'general' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-navy-900">Primary Challenge</label>
                    <select name="challenge" className="w-full px-4 py-3 rounded-lg border border-slate-200 outline-none">
                      <option>CQC Turnaround</option>
                      <option>Business Expansion</option>
                      <option>Quality Assurance</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-navy-900">Current CQC Rating</label>
                    <select name="rating" className="w-full px-4 py-3 rounded-lg border border-slate-200 outline-none">
                      <option>Outstanding</option>
                      <option>Good</option>
                      <option>Requires Improvement</option>
                      <option>Inadequate</option>
                    </select>
                  </div>
                </div>
              )}

              {activeForm === 'training' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-navy-900">Number of Staff</label>
                    <input type="number" name="staff_count" className="w-full px-4 py-3 rounded-lg border border-slate-200" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-navy-900">Preferred Format</label>
                    <select name="format" className="w-full px-4 py-3 rounded-lg border border-slate-200 outline-none">
                      <option>On-site</option>
                      <option>Virtual</option>
                      <option>Hybrid</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-bold text-navy-900">Additional Details</label>
                <textarea name="message" rows="4" className="w-full px-4 py-3 rounded-lg border border-slate-200 outline-none"></textarea>
              </div>

              <button type="submit" className="w-full bg-gold-500 text-navy-900 py-4 rounded-xl font-bold text-lg hover:bg-gold-400 transition-all shadow-lg shadow-gold-500/20 active:scale-[0.98]">
                Submit Application
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact;